import React, { useState, useEffect, useRef } from "react";
import StudioHero from "../../components/StudioHero";
import StudioCanvas from "../../components/StudioCanvas";
import StudioGenerateButton from "../../components/StudioGenerateButton";
import StudioResultGrid from "../../components/StudioResultGrid";
import {
  runStudioWorkflow,
  getStudioWorkflowJobDetail,
  retryStudioIteration,
} from "../../../../../actions/studios";
import { getUroSocket } from "../../../../../services/URO";
import { uploadImage } from "../../../../../actions/imageUpload";
import "./GameAssetCreatorStudio.css";

// Hardcoded workflow_definition_id for Game Asset Creator (seeded once).
const WORKFLOW_ID = "c572dd27-a4e6-4508-a568-78997f1cbb9f";
const MAP_NODE_ID = "map-generate";

/**
 * Read the map node's iterations array from a workflow-job-detail response.
 */
function readMapIterations(detailResponse) {
  if (!detailResponse || !Array.isArray(detailResponse.steps)) return null;
  const mapStep = detailResponse.steps.find(
    (s) => s.workflow_step_id === MAP_NODE_ID
  );
  if (!mapStep || !mapStep.output_results) return null;

  const output =
    typeof mapStep.output_results === "string"
      ? JSON.parse(mapStep.output_results)
      : mapStep.output_results;

  return Array.isArray(output?.iterations) ? output.iterations : null;
}

export default function GameAssetCreatorStudio({ studio }) {
  const [userRequest, setUserRequest] = useState("");
  const [referenceImageUrl, setReferenceImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  // status: idle | running | complete | failed
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);

  const [workflowJobId, setWorkflowJobId] = useState(null);
  const [iterations, setIterations] = useState([]);

  const socketRef = useRef(null);

  // Subscribe to socket events for the current workflow job
  useEffect(() => {
    if (!workflowJobId) return;

    const socket = getUroSocket();
    if (!socket) return;
    socketRef.current = socket;

    const handleComplete = async (payload) => {
      if (payload?.workflow_job_id !== workflowJobId) return;
      try {
        const detail = await getStudioWorkflowJobDetail(workflowJobId);
        const freshIterations = readMapIterations(detail);
        if (freshIterations) setIterations(freshIterations);
        setStatus("complete");
      } catch (err) {
        console.error("Failed to fetch completed workflow detail", err);
        setStatus("complete");
      }
    };

    const handleFailed = async (payload) => {
      if (payload?.workflow_job_id !== workflowJobId) return;
      try {
        const detail = await getStudioWorkflowJobDetail(workflowJobId);
        const freshIterations = readMapIterations(detail);
        if (freshIterations) setIterations(freshIterations);
      } catch (_) {}
      setErrorMessage(
        payload?.failed_message || payload?.error || "Workflow failed"
      );
      setStatus("failed");
    };

    const handleIterationRetried = (payload) => {
      if (payload?.workflow_job_id !== workflowJobId) return;
      if (!payload?.map_output?.iterations) return;
      setIterations(payload.map_output.iterations);
    };

    // When the llm-planner step completes, its output contains the parsed
    // JSON with the prompts array. We use that to pre-populate processing
    // cards so the user sees how many images are being generated.
    const handleStepComplete = (payload) => {
      if (payload?.workflow_job_id !== workflowJobId) return;
      if (payload?.node_id === "llm-planner" && payload?.output?.parsed) {
        const prompts = payload.output.parsed.prompts;
        if (Array.isArray(prompts) && prompts.length > 0) {
          setIterations(
            prompts.map((_, idx) => ({
              status: "processing",
              iteration_index: idx,
            }))
          );
        }
      }
    };

    socket.on("workflow:complete", handleComplete);
    socket.on("workflow:failed", handleFailed);
    socket.on("workflow:iteration:retried", handleIterationRetried);
    socket.on("workflow:step:complete", handleStepComplete);

    // Fallback polling. Covers two scenarios:
    //   1. Socket missed an event → poll catches the terminal status
    //   2. The llm-planner step:complete event was missed (likely — it fires
    //      before the useEffect registers the listener) → poll detects the
    //      planner step's output and pre-populates processing cards.
    //
    // Polls every 5 seconds (fast enough to feel responsive for the planner
    // detection, which matters for UX — the user wants to see cards appear
    // shortly after clicking Generate).
    const pollInterval = setInterval(async () => {
      try {
        const detail = await getStudioWorkflowJobDetail(workflowJobId);
        if (!detail) return;

        // If we don't have iterations yet, check if the planner step has
        // completed — its output_results.parsed.prompts tells us how many
        // cards to show. This fires within 5-10s of the planner finishing,
        // even if the socket event was missed.
        if (detail.steps && iterations.length === 0) {
          const plannerStep = detail.steps.find(
            (s) => s.workflow_step_id === "llm-planner" && s.status === "completed"
          );
          if (plannerStep?.output_results?.parsed?.prompts) {
            const prompts = plannerStep.output_results.parsed.prompts;
            if (Array.isArray(prompts) && prompts.length > 0) {
              setIterations(
                prompts.map((_, idx) => ({
                  status: "processing",
                  iteration_index: idx,
                }))
              );
            }
          }
        }

        // Update iterations from the map step if available.
        const freshIterations = readMapIterations(detail);
        if (freshIterations && freshIterations.length > 0) {
          setIterations(freshIterations);
        }

        if (detail.status === "completed") {
          if (freshIterations) setIterations(freshIterations);
          setStatus("complete");
          clearInterval(pollInterval);
        } else if (detail.status === "failed") {
          if (freshIterations) setIterations(freshIterations);
          setErrorMessage(
            detail.error_message || detail.failed_message || "Workflow failed"
          );
          setStatus("failed");
          clearInterval(pollInterval);
        }
      } catch (_) {}
    }, 5000);

    return () => {
      socket.off("workflow:complete", handleComplete);
      socket.off("workflow:failed", handleFailed);
      socket.off("workflow:iteration:retried", handleIterationRetried);
      socket.off("workflow:step:complete", handleStepComplete);
      clearInterval(pollInterval);
    };
  }, [workflowJobId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErrorMessage(null);
    try {
      const result = await uploadImage(file);
      const url =
        typeof result === "string"
          ? result
          : result?.image_url ||
            result?.url ||
            result?.data?.image_url ||
            result?.data?.url;
      if (!url) {
        throw new Error(
          `Upload succeeded but no URL returned: ${JSON.stringify(result)}`
        );
      }
      setReferenceImageUrl(url);
    } catch (err) {
      console.error("Image upload failed", err);
      setErrorMessage(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const canGenerate =
    !!userRequest.trim() &&
    !!referenceImageUrl &&
    status !== "running";

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setStatus("running");
    setErrorMessage(null);
    setIterations([]);

    try {
      // The workflow now handles everything — planner, generation, bg removal.
      // We just send the user's raw request + reference image.
      const runResult = await runStudioWorkflow(WORKFLOW_ID, {
        user_request: userRequest.trim(),
        reference_image_url: referenceImageUrl,
      });

      setWorkflowJobId(runResult.workflow_job_id);
    } catch (err) {
      setErrorMessage(err.message || "Generation failed");
      setStatus("failed");
    }
  };

  const handleRetry = async (iterationIndex) => {
    if (!workflowJobId) return;

    setIterations((prev) =>
      prev.map((it, idx) =>
        (it?.iteration_index ?? idx) === iterationIndex
          ? { status: "processing", iteration_index: iterationIndex }
          : it
      )
    );

    try {
      const result = await retryStudioIteration(
        workflowJobId,
        MAP_NODE_ID,
        iterationIndex
      );
      if (result?.map_output?.iterations) {
        setIterations(result.map_output.iterations);
      }
    } catch (err) {
      setIterations((prev) =>
        prev.map((it, idx) =>
          (it?.iteration_index ?? idx) === iterationIndex
            ? {
                status: "failed",
                error: err.message || "Retry failed",
                iteration_index: iterationIndex,
              }
            : it
        )
      );
    }
  };

  const handleReset = () => {
    setStatus("idle");
    setErrorMessage(null);
    setIterations([]);
    setWorkflowJobId(null);
  };

  // Canvas state mapping. When failed with per-iteration data, show the grid
  // so per-card retry buttons are visible instead of the generic error overlay.
  let canvasStatus = "idle";
  let loadingMessage = "";
  if (status === "running") {
    // Show "processing" until we have iteration data, then switch to
    // "complete" so the grid renders (cards show their own spinners).
    const hasIterationData = iterations.length > 0;
    canvasStatus = hasIterationData ? "complete" : "processing";
    loadingMessage = hasIterationData ? "" : "Planning & generating...";
  } else if (status === "complete") {
    canvasStatus = "complete";
  } else if (status === "failed") {
    const hasIterationData = iterations.some(
      (it) => it && it.status !== "processing"
    );
    canvasStatus = hasIterationData ? "complete" : "failed";
  }

  return (
    <div className="game-asset-creator-studio">
      <StudioHero
        title={studio.name}
        subtitle="Pilot Studio • Graph Engine v2"
        description={studio.description}
      />

      <div className="gac-layout">
        <div className="gac-form-panel">
          <label className="gac-field">
            <span className="gac-field-label">What do you want to create?</span>
            <textarea
              className="gac-textarea"
              value={userRequest}
              onChange={(e) => setUserRequest(e.target.value)}
              placeholder={
                "e.g. Generate 5 upgrade tiers of this townhall, each looking more advanced and fortified than the last"
              }
              rows={4}
              disabled={status === "running"}
            />
          </label>

          <label className="gac-field">
            <span className="gac-field-label">Reference image</span>
            {referenceImageUrl ? (
              <div className="gac-image-preview">
                <img src={referenceImageUrl} alt="Reference" />
                <button
                  className="gac-image-clear"
                  onClick={() => setReferenceImageUrl(null)}
                  disabled={status === "running"}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="gac-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {uploading && <div className="gac-upload-hint">Uploading...</div>}
              </div>
            )}
          </label>

          <StudioGenerateButton
            onClick={handleGenerate}
            disabled={!canGenerate}
            loading={status === "running"}
            loadingLabel="Generating..."
          />

          {status === "failed" && (
            <button className="gac-reset-btn" onClick={handleReset}>
              Reset
            </button>
          )}
        </div>

        <div className="gac-canvas-panel">
          <StudioCanvas
            status={canvasStatus}
            emptyMessage="Your game assets will appear here"
            emptyIcon="🎮"
            loadingMessage={loadingMessage}
            errorMessage={errorMessage}
            onRetry={status === "failed" ? handleReset : undefined}
          >
            {status === "failed" && errorMessage && (
              <div className="gac-error-banner">
                <span className="gac-error-banner-icon">⚠</span>
                <span className="gac-error-banner-text">{errorMessage}</span>
              </div>
            )}
            <StudioResultGrid items={iterations} onRetry={handleRetry} />
          </StudioCanvas>
        </div>
      </div>
    </div>
  );
}
